import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import QueryKeys from '../utils/constants/queryKeys'
import getOrg from '../Apis/organisation/getOrg'
import useQueryParams from './useQueryParams'
import getGenderSpec from '../Apis/team/getGenderSpec'
import getAgeRestriction from '../Apis/team/getAgeRestriction'
import getTeams from '../Apis/team/getTeams'
import addTeam from '../Apis/team/addTeam'
import addSeasonTeam from '../Apis/season/addSeasonTeam'
import updateTeam from '../Apis/team/updateTeam'
import deleteTeam from '../Apis/team/deleteTeam'
import getSingleTeam from '../Apis/team/getSingleTeam'
import localStorageHelper from '../utils/helpers/localStorageHelper'
import useHistoryPush from './useHistoryPush'
import localStorageIds from '../utils/constants/localStorageIds'
import useOrgSubscriptionStatus from './useOrgSubscriptionStatus'

const useTeamsSettings = (selectedTeam) => {
  const [showAddTeamModal, setShowAddTeamModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isUpdateSnackbarOpen, setIsUpdateSnackbarOpen] = useState(false)
  const queryParams = useQueryParams()
  const { org } = queryParams
  const queryClient = useQueryClient()
  const { redirect } = useHistoryPush()
  const teamLocalStorageHelper = localStorageHelper(localStorageIds.SELECTED_TEAM)
  const seasonLocalStorageHelper = localStorageHelper(localStorageIds.SELECTED_SEASON)
  const seasonId = seasonLocalStorageHelper.localStorageValue?.id

  const singleOrg = useQuery([QueryKeys.GET_SINGLE_ORG, org], () => getOrg(org), {
    enabled: !!org,
  })

  useOrgSubscriptionStatus(org)

  const teams = useQuery([QueryKeys.GET_TEAMS, org || null], () => org && getTeams(org), {enabled: false})

  const team = useQuery(
    [QueryKeys.GET_SINGLE_TEAM, org, selectedTeam],
    () => selectedTeam && getSingleTeam(org, selectedTeam),
  )

  const gender = useQuery([QueryKeys.GET_GENDER_SPEC], () => getGenderSpec())

  const ageRestriction = useQuery([QueryKeys.GET_AGE_RESTRICTION], () => getAgeRestriction())

  const addATeam = useMutation((data) => addTeam(data), {
    onSuccess: ({ data }) => {
      setShowAddTeamModal(false)
      teamLocalStorageHelper.setLocalStorageValue(data)
      return queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
    },
  })

  const addASeasonTeam = useMutation((data) => addSeasonTeam(data), {
    onSuccess: ({ data }) => {
      setShowAddTeamModal(false)
      teamLocalStorageHelper.setLocalStorageValue(data)
      return queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
    },
  })

  const updateATeam = useMutation((data) => updateTeam({ ...data, orgId: org }), {
    onSuccess: (res) => {
      setIsUpdateSnackbarOpen(true)
      setShowAddTeamModal(false)
      teamLocalStorageHelper.setLocalStorageValue(res.data)
      queryClient.invalidateQueries(QueryKeys.GET_SINGLE_TEAM)
      queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
    },
  })

  const deleteATeam = useMutation((data) => deleteTeam({ ...data, orgId: org }), {
    onSuccess: () => {
      setShowDeleteModal(false)
      queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
      queryClient.invalidateQueries(QueryKeys.GET_ORGS)

      // When deleting last org.
      if (teams?.data?.length === 1) {
        teamLocalStorageHelper.removeLocalStorageValue()
        redirect('/calendar', {}, ['team'])
      }
    },
  })

  return {
    singleOrgLogo: singleOrg?.data?.logoUrl,
    singleOrgName: singleOrg?.data?.name,
    orgId: singleOrg?.data?.id,
    seasonId,
    teams:
      teams?.data?.length > 0 &&
      teams?.data?.sort((a, b) =>
        a.name.toUpperCase() > b.name.toUpperCase()
          ? 1
          : b.name.toUpperCase() > a.name.toUpperCase()
            ? -1
            : 0,
      ),
    team: team?.data,
    gender: gender?.data,
    ageRestriction: ageRestriction?.data,
    addTeam: addATeam,
    addSeasonTeam: addASeasonTeam,
    updateTeam: updateATeam,
    deleteTeam: deleteATeam,
    showAddTeamModal,
    setShowAddTeamModal,
    showDeleteModal,
    setShowDeleteModal,
    isUpdateSnackbarOpen,
    setIsUpdateSnackbarOpen,
  }
}

export default useTeamsSettings
